// apps/dashboard/src/components/consent/ConsentBanner.tsx
'use client';

import { useTranslations } from 'next-intl';
import { setAnalyticsConsent } from '@/lib/consent';
import { useConsentPrompt } from '@/lib/use-consent';

// The privacy policy lives on the landing site, not in this app.
const LANDING_URL = process.env.NEXT_PUBLIC_LANDING_URL || 'http://localhost:3503';
const PRIVACY_HREF = `${LANDING_URL}/kebijakan-privasi`;

// Equal visual weight on purpose (same style for both choices): declining must be
// exactly as easy and as prominent as accepting.
const CHOICE_BUTTON =
  'flex-1 sm:flex-none sm:min-w-24 inline-flex items-center justify-center rounded-lg border border-teal-600 bg-white px-4 py-2 text-sm font-semibold text-teal-700 transition-colors hover:bg-teal-50';

/**
 * Opt-in analytics consent bar. Overlays the bottom of the viewport (no layout shift),
 * stays until a choice is made, and renders nothing when a choice is stored or the
 * browser sends a DNT/GPC signal (already a denial). Client-only by construction:
 * useConsentPrompt() is `false` on the server and during hydration.
 */
export function ConsentBanner() {
  const t = useTranslations('consent');
  const shouldPrompt = useConsentPrompt();

  if (!shouldPrompt) return null;

  return (
    <div
      role="region"
      aria-label={t('regionLabel')}
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-slate-200 bg-white pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 shadow-[0_-4px_16px_rgba(15,23,42,0.08)]"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 sm:flex-row sm:items-center sm:gap-6 sm:px-6 lg:px-8">
        <p className="text-xs leading-relaxed text-slate-600 sm:flex-1 sm:text-sm">
          {t('message')}{' '}
          <a
            href={PRIVACY_HREF}
            className="font-medium text-teal-700 underline underline-offset-2 hover:text-teal-800"
          >
            {t('privacyLink')}
          </a>
        </p>
        <div className="flex gap-2 sm:shrink-0">
          <button type="button" className={CHOICE_BUTTON} onClick={() => setAnalyticsConsent('denied')}>
            {t('decline')}
          </button>
          <button type="button" className={CHOICE_BUTTON} onClick={() => setAnalyticsConsent('granted')}>
            {t('accept')}
          </button>
        </div>
      </div>
    </div>
  );
}
